import { observable, configure, action } from 'mobx';
import util from 'preact-util';
import LocalModel from './localModel';

configure({ enforceActions: 'always' });

class RecipientListStore extends LocalModel {
    constructor() {
        super('recipientList', {
            namePlural: 'recipientLists',      // observable name
            namePluralReal: 'recipientlists',  // API url segment
            sort: 'name',
            limit: 100,
            api: {
                search: { url: '/api/recipientlists/', params: { limit: 100, sort: 'name' } },
                load: { url: '/api/recipientlists/', params: {} },
                save: { url: '/api/recipientlists/', params: {} },
                delete: { url: '/api/recipientlists/', params: {} },
            },
        });
    }

    @observable recipientLists = [];

    @observable recipientList = {};

    @observable newRecipientList = {};

    @observable resolvePreview = {};

    @observable resolveFailed = false;

    @observable members = {};

    @observable groupCatalogue = [];

    @action
    async loadLists() {
        const response = await util.fetchApi('/api/recipientlists/', { method: 'GET' }, { limit: 100, sort: 'name' });
        if (response?.status === 200) {
            this.updateKeyValue('recipientLists', response.data || []);
        }
        return response;
    }

    @action
    async loadList(id) {
        const response = await util.fetchApi(`/api/recipientlists/${id}`, { method: 'GET' });
        if (response?.status === 200) {
            const doc = Array.isArray(response.data) ? response.data[0] : response.data;
            this.updateKeyValue('recipientList', doc || {});
            return doc;
        }
        return null;
    }

    @action
    async createList({ name }) {
        const response = await util.fetchApi('/api/recipientlists/', { method: 'POST' }, { name });
        if (response?.status === 201) {
            await this.loadLists();
            return response.data?.recipientList || null;
        }
        return null;
    }

    @action
    async saveField(id, key, value) {
        const response = await util.fetchApi(`/api/recipientlists/${id}`, { method: 'PATCH' }, { [key]: value });
        if (response?.status === 202) {
            this.updateKeyValue('recipientList', response.data?.recipientList || {});
        }
        return response;
    }

    @action
    async addMember(id, kind, memberId) {
        const response = await util.fetchApi(`/api/recipientlists/${id}/members`, { method: 'POST' }, { kind, id: memberId });
        if (response?.status === 202) {
            this.updateKeyValue('recipientList', response.data?.recipientList || {});
        }
        return response;
    }

    @action
    async removeMember(id, kind, memberId) {
        const response = await util.fetchApi(`/api/recipientlists/${id}/members/${kind}/${memberId}`, { method: 'DELETE' });
        if (response?.status === 200) {
            await this.loadList(id);
        }
        return response;
    }

    @action
    async resolve(id) {
        const response = await util.fetchApi(`/api/recipientlists/${id}/resolve`, { method: 'GET' }, { limit: 50 });
        if (response?.status === 200) {
            this.updateKeyValue('resolvePreview', response.data || {});
            this.updateKeyValue('resolveFailed', false);
            return response;
        }
        // A failed preview must NOT read as "0 mottakere". resolvePreview is a single
        // shared observable, so leaving it untouched shows either {} (0 recipients,
        // 0 skipped) on first load or the PREVIOUS list's numbers when switching
        // lists. An unknown group key is deliberately designed to surface here as a
        // 500 — reporting that as an empty list is the one place an admin looks.
        this.updateKeyValue('resolvePreview', {});
        this.updateKeyValue('resolveFailed', true);
        return response;
    }

    @action
    async loadMembers(id) {
        const response = await util.fetchApi(`/api/recipientlists/${id}/members`, { method: 'GET' });
        if (response?.status === 200) {
            this.updateKeyValue('members', response.data || {});
        }
        return response;
    }

    @action
    async loadGroupCatalogue() {
        if (this.groupCatalogue.length > 0) {
            return null;
        }
        const response = await util.fetchApi('/api/recipientlists/groups', { method: 'GET' });
        if (response?.status === 200) {
            this.updateKeyValue('groupCatalogue', response.data || []);
        }
        return response;
    }

    @action
    async addGroup(id, key, months) {
        const response = await util.fetchApi(`/api/recipientlists/${id}/groups`, { method: 'POST' }, { key, months });
        if (response?.status === 202) {
            this.updateKeyValue('recipientList', response.data?.recipientList || {});
        }
        return response;
    }

    @action
    async removeGroup(id, key) {
        const response = await util.fetchApi(`/api/recipientlists/${id}/groups/${encodeURIComponent(key)}`, { method: 'DELETE' });
        if (response?.status === 200) {
            this.updateKeyValue('recipientList', response.data?.recipientList || {});
        }
        return response;
    }
}

const store = new RecipientListStore();
export default store;
